import { SecretConfig } from '@stores/source';
import { UnknownObject } from '@utils/types';
import { merge } from 'lodash';
import moment from 'moment';
import { JSONSchema, UISchema } from './jsonSchemaForm';
/*

generateUISchemaFromProps takes a jsonSchema and generate the required uiSchema
https://react-jsonschema-form.readthedocs.io/en/latest/api-reference/uiSchema/

secret: We need to add password widget in case that a property is a secret and add placeholder
  if secretConfig masked value exists
date: We need to add a date picker in case that a property is a date
const values: are values that will not suppose to be changed.
  We need to hide these values from the form
Example:
  input: {properties: {
    auth_type: { type:"string", const: 'oauth2.0'},
    password:{ type:"string", secret:true },
    start_date:{ type:"string" }
  }}
  output: {
      auth_type:{'ui:widget': 'hidden'},
      password:{'ui:widget': 'password'},
      start_date:{'ui:widget': 'date'},
    }
*/
const generateUISchemaFromProps = (schema: JSONSchema, secretConfig?: SecretConfig) => {
  const schemaProperties = schema.properties || {};
  const dateFieldNames = [
    'start_date',
    'end_date',
    'begin_date',
    'begin_time',
    'end_time',
    'start_time',
    'date',
  ];
  return Object.keys(schemaProperties).reduce((uiSchema, propName: string) => {
    const currentProp = schemaProperties[propName];
    if (currentProp.type === 'object' && currentProp.properties) {
      uiSchema[propName] = generateUISchemaFromProps(currentProp as JSONSchema, secretConfig);
    } else if (currentProp.type === 'object' && currentProp.oneOf) {
      uiSchema[propName] = currentProp.oneOf.reduce((occum2, obj) => {
        if (obj.properties) {
          occum2 = { ...occum2, ...generateUISchemaFromProps(obj, secretConfig) };
        }
        return occum2;
      }, {} as UISchema);
    } else if (currentProp.airbyte_secret || currentProp.secret || currentProp.rudder_secret) {
      uiSchema[propName] = { 'ui:widget': 'password' };
      if (secretConfig && secretConfig[propName]) {
        uiSchema[propName]['ui:placeholder'] = secretConfig[propName] as UnknownObject;
      }
    } else if (dateFieldNames.includes(propName)) {
      uiSchema[propName] = {
        'ui:widget': 'date',
        'ui:options': {
          format:
            (currentProp?.examples &&
              currentProp?.examples[0] &&
              (moment(currentProp?.examples[0]) as unknown as { _f: unknown })._f) ||
            'iso',
        },
      };
    } else if (currentProp.const) {
      currentProp.default = currentProp.default || currentProp.const;
      uiSchema[propName] = {
        'ui:widget': 'hidden',
      };
    }

    return uiSchema as UISchema;
  }, {} as UISchema);
};

export const getDefaultUISchema = (schema: JSONSchema, secretConfig?: SecretConfig) => {
  const UiSchemaFromProps = generateUISchemaFromProps(schema, secretConfig);
  // Here is the root object of UISchema, we can define static options
  // https://react-jsonschema-form.readthedocs.io/en/latest/api-reference/uiSchema/#uischema
  const staticUISchema = {
    /*
    This disables the additionalProperties https://react-jsonschema-form.readthedocs.io/en/latest/usage/objects/#additional-properties
    Some connector's specification includes { additionalProperties:true } that has as a result that
    the form generates a "+" button for adding more properties. This is not something we use somehow. 
    */
    'ui:options': {
      expandable: false,
    },
  };
  return merge({}, staticUISchema, UiSchemaFromProps);
};

const sortSchemaByOrder = (schema: JSONSchema) => {
  const propertiesWithOrder: [string, JSONSchema][] = [];

  const propertiesWithoutOrder: [string, JSONSchema][] = [];
  // split the properties that have order and those that don't
  Object.entries(schema.properties || {}).forEach((val) => {
    const [, propSchema] = val;
    if (typeof propSchema.order === 'number') {
      propertiesWithOrder.push(val);
    } else {
      propertiesWithoutOrder.push(val);
    }
  });
  // sort properties with order
  propertiesWithOrder.sort(([, a], [, b]) => a.order! - b.order!);
  // rebuild the schema with the correct order
  schema.properties = [...propertiesWithOrder, ...propertiesWithoutOrder].reduce(
    (occum, [key, propSchema]) => ({ ...occum, [key]: propSchema }),
    {},
  );
  return schema;
};

/*
Set a field as optional if is included in the secret config.
We need this for updating the configuration without requiring a secret value, so
the form to be enabled.

Examples
input: { required:["start_date", "api_key"], properties:{...} }, { api_key: "***********htYi" }
output: { required:["start_date"], properties:{...} }

*/
const removeRequiredFromFieldsWithSecretConfig = (
  schema: JSONSchema,
  secretConfig?: SecretConfig,
) => {
  if (!secretConfig) {
    return schema;
  }
  const secretFields = Object.keys(secretConfig);
  schema.required = schema.required?.filter((f) => !secretFields.includes(f));
  return schema;
};

/*
A generic function that handles all the transformation the JSON schema need like sort.
*/
export const transformJSONSchema = (schema: JSONSchema, secretConfig?: SecretConfig) =>
  sortSchemaByOrder(removeRequiredFromFieldsWithSecretConfig(schema, secretConfig));
